import React from "react";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useI18n } from "@/i18n";
import { Clock, Users, CheckCircle2, Sparkles } from "lucide-react";

const priorityStyle = {
  High: "bg-rose-500/15 text-rose-600 border-rose-500/30",
  Medium: "bg-amber-500/15 text-amber-600 border-amber-500/30",
  Low: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
};

export function Recommendations({ recommendations }) {
  const { t } = useI18n();
  if (!recommendations) return null;
  const { overall_summary, programs = [], top_gaps = [] } = recommendations;

  const tierName = (p) => {
    const key = `tier.${p.tier_key}`;
    const v = t(key);
    return v === key ? p.tier_label || "" : v;
  };

  return (
    <div className="space-y-6" data-testid="recommendations-panel">
      <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <Card className="bg-gradient-to-br from-indigo-500/10 to-slate-900 border-indigo-500/30 p-6">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={18} className="text-indigo-600" />
            <span className="text-xs uppercase tracking-widest font-semibold text-indigo-600">
              {t("rec.aiSummary")}
            </span>
          </div>
          <p className="text-slate-800 leading-relaxed text-sm sm:text-base" data-testid="ai-summary">
            {overall_summary}
          </p>
          {top_gaps.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mt-4">
              <span className="text-xs text-slate-500">{t("rec.priorityGaps")}</span>
              {top_gaps.map((g) => (
                <Badge key={g} className="bg-rose-500/15 text-rose-300 border-rose-500/30">
                  {g}
                </Badge>
              ))}
            </div>
          )}
        </Card>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {programs.map((p, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: i * 0.05 }}
          >
            <Card
              className="bg-white border-slate-200 p-6 h-full transition-transform duration-200 hover:-translate-y-0.5 hover:border-indigo-500/50"
              data-testid={`program-card-${p.tier_key}`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <p className="text-xs uppercase tracking-widest font-semibold text-amber-600 mb-1">
                    {tierName(p)}
                  </p>
                  <h3 className="font-heading text-xl font-bold text-slate-900 leading-snug">{p.program_name}</h3>
                </div>
                <Badge className={`shrink-0 border ${priorityStyle[p.priority] || priorityStyle.Medium}`}>
                  {t(`priority.${p.priority}`)}
                </Badge>
              </div>

              <p className="text-sm text-slate-600 leading-relaxed mb-4">{p.rationale}</p>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <Metric icon={Users} label={t("rec.format")} value={p.format} />
                <Metric icon={Clock} label={t("rec.duration")} value={t("rec.months", { n: p.duration_months })} />
              </div>

              {p.focus_phases?.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid={`focus-phases-${p.tier_key}`}>
                  <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">5D</span>
                  {p.focus_phases.map((f) => (
                    <span
                      key={f}
                      className="px-2 py-0.5 rounded-md text-[11px] font-semibold bg-amber-500/10 text-amber-300 border border-amber-500/20"
                    >
                      {f}
                    </span>
                  ))}
                </div>
              )}

              {p.focus_competencies?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {p.focus_competencies.map((f) => (
                    <span
                      key={f}
                      className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-500/10 text-indigo-700 border border-indigo-500/20"
                    >
                      {f}
                    </span>
                  ))}
                </div>
              )}

              {p.modules?.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-2">
                    {t("rec.modules")}
                  </p>
                  <ul className="space-y-1.5">
                    {p.modules.map((m, mi) => (
                      <li key={mi} className="flex items-start gap-2 text-sm text-slate-700">
                        <CheckCircle2 size={15} className="text-emerald-600 mt-0.5 shrink-0" />
                        <span>{m}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {p.phases?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <p className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-2">
                    {t("rec.phases")}
                  </p>
                  <div className="space-y-2">
                    {p.phases.map((ph, pi) => (
                      <div key={pi} className="flex items-center gap-3 text-sm">
                        <span className="font-mono text-xs text-indigo-600 w-16 shrink-0">
                          M{ph.month_start}–M{ph.month_end}
                        </span>
                        <span className="text-slate-700">
                          <strong className="text-slate-900">{ph.name}:</strong> {ph.description}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value, small }) {
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
      <div className="flex items-center gap-1.5 text-slate-500 mb-1">
        <Icon size={13} />
        <span className="text-[10px] uppercase tracking-wide font-semibold">{label}</span>
      </div>
      <p className={`text-slate-900 font-semibold ${small ? "text-xs leading-snug" : "text-sm"}`}>{value}</p>
    </div>
  );
}
